import PropTypes from 'prop-types';
import styled from 'styled-components';

import { config } from '_config';
import { theme } from '_constants/theme';

import * as Layout from '_components/atomic/layouts';
import StartBanner from '../StartBanner';
import JoinBanner from '../JoinBanner';

export const howToFormAnLLCStepByStepGuideContents = [
  'What is an LLC?',
  'Preparing to Form Your LLC',
  'The Formation Process',
];

const StyledContainer = styled.div`
  position: relative;
`;

const StyledBlock = styled.div`
  position: relative;
  z-index: 1;

  ul {
    padding-top: ${theme.spaces.regular}px;

    li {
      margin-bottom: ${theme.spaces.regular}px;

      b {
        text-decoration: underline;
      }
    }
  }
`;

const StyledBackgroundImageContainer = styled.div`
  position: absolute;
  top: 50%;
  width: 100%;

  img {
    width: 100%;
  }
`;

const HowToFormAnLLCStepByStepGuideArticle = ({ idPrefix }) => {
  return (
    <StyledContainer>
      <Layout.Card.Text>
        Forming a Limited Liability Company (LLC) is a significant step for many
        entrepreneurs. LLCs offer a combination of liability protection and tax
        flexibility that can benefit small business owners. In this
        comprehensive guide,&nbsp;
        <b>we&apos;ll walk you through the steps of forming an LLC</b>, ensuring
        you understand the process from start to finish. By the end of this
        article, you&apos;ll be equipped with the knowledge needed to form your
        own LLC confidently.
      </Layout.Card.Text>
      <StyledBlock id={`${idPrefix}-${0}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          1. What is an LLC?
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Definition and Overview
        </Layout.Card.Text>
        <Layout.Card.Text>
          A Limited Liability Company (LLC) is a business structure that
          combines the limited liability of a corporation with the tax
          efficiencies and operational flexibility of a partnership. Owners of
          an LLC are referred to as members, and there can be one or more
          members in an LLC. Advantages of an LLC over Other Business
          Structures:
          <ul>
            <li>
              <b>Limited Liability Protection:</b> Members are protected from
              personal liability for business debts and claims, meaning personal
              assets are generally not at risk.
            </li>
            <li>
              <b>Tax Flexibility:</b> LLCs can choose to be taxed as a sole
              proprietorship, partnership, S corporation, or C corporation,
              depending on what best suits the business.
            </li>
            <li>
              <b>Operational Flexibility:</b> Unlike corporations, LLCs are not
              required to hold annual meetings or keep extensive records.
            </li>
            <li>
              <b>Ease of Formation and Maintenance:</b> Forming and maintaining
              an LLC is relatively straightforward compared to corporations.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${1}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          2. Preparing to Form Your LLC
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Choosing a Name
        </Layout.Card.Text>
        <Layout.Card.Text>
          Selecting a name for your LLC is the first step. Your LLC&apos;s name
          must be unique and comply with your state&apos;s regulations. Here are
          some tips for choosing the right name:
          <br />
          <ul>
            <li>
              <b>Uniqueness:</b> Ensure the name is distinguishable from other
              registered businesses in your state.
            </li>
            <li>
              <b>Compliance:</b> Follow your state’s naming rules, which
              typically include using &quot;LLC&quot; or &quot;Limited Liability
              Company&quot; in the name.
            </li>
            <li>
              <b>Trademark Search:</b> Perform a trademark search to avoid
              potential legal issues.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Understanding State-Specific Requirements
        </Layout.Card.Text>
        <Layout.Card.Text>
          Each state has its own rules for forming an LLC. Familiarize yourself
          with your state&apos;s requirements, which may include filing specific
          forms and paying certain fees.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Appointing a Registered Agent
        </Layout.Card.Text>
        <Layout.Card.Text>
          A registered agent is a person or business entity authorized to
          receive legal documents on behalf of your LLC. This agent must have a
          physical address in the state where your LLC is formed. Choosing a
          reliable registered agent ensures you receive important legal notices
          in a timely manner.
        </Layout.Card.Text>
        <StartBanner />
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${2}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          3. The Formation Process
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Filing Articles of Organization
        </Layout.Card.Text>
        <Layout.Card.Text>
          The Articles of Organization (or Certificate of Formation in some
          states) is the document you file with the state to officially form
          your LLC. This document typically includes:
          <ul>
            <li>
              <b>LLC Name:</b> The legal name of your LLC.
            </li>
            <li>
              <b>Principal Address:</b> The primary location of your business.
            </li>
            <li>
              <b>Registered Agent:</b> The name and address of your registered
              agent. Management Structure: Indicate whether your LLC will be
              managed by members or managers.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Creating an Operating Agreement
        </Layout.Card.Text>
        <Layout.Card.Text>
          An Operating Agreement is an internal document that outlines the
          ownership and operating procedures of the LLC. While not always
          required by state law, it is highly recommended as it helps prevent
          disputes by clearly defining each member&apos;s role and
          responsibilities. Key elements to include:
          <ul>
            <li>
              <b>Ownership Structure:</b> Detail the ownership percentages of
              each member.
            </li>
            <li>
              <b>Management Responsibilities:</b> Define who will manage the LLC
              and how decisions will be made. Profit Distribution: Specify how
              profits and losses will be allocated among members.
            </li>
            <li>
              <b>Meeting Requirements:</b> Outline the frequency and process for
              member meetings.
            </li>
            <li>
              <b>Dissolution Process:</b> Establish procedures for dissolving
              the LLC if necessary.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Obtaining an EIN (Employer Identification Number)
        </Layout.Card.Text>
        <Layout.Card.Text>
          An EIN, or Employer Identification Number, is a federal tax ID number
          that the IRS uses to identify your business. You need an EIN to open a
          business bank account, file taxes, and hire employees. You can obtain
          an EIN for free from the IRS through their online application process.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Opening a Business Bank Account
        </Layout.Card.Text>
        <Layout.Card.Text>
          Keeping your business finances separate from personal finances is
          crucial for maintaining your LLC&apos;s limited liability protection.
          To open a business bank account, you typically need your EIN, Articles
          of Organization, and Operating Agreement.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Complying with Tax Requirements
        </Layout.Card.Text>
        <Layout.Card.Text>
          LLCs have various tax obligations depending on how they are
          structured. It&apos;s essential to understand and comply with federal,
          state, and local tax requirements:
          <ul>
            <li>
              <b>Federal Taxes:</b> Depending on your tax classification, you
              may need to file Form 1040 (Schedule C), Form 1065, Form 1120, or
              Form 1120S.
            </li>
            <li>
              <b>State Taxes:</b> Be aware of state income taxes, franchise
              taxes, and sales taxes applicable to your business.
            </li>
            <li>
              <b>Local Taxes:</b> Some localities impose additional taxes or
              fees on businesses.
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Maintaining Compliance with State Laws
        </Layout.Card.Text>
        <Layout.Card.Text>
          To remain in good standing, LLCs must comply with ongoing state
          requirements, which may include:
          <ul>
            <li>
              Annual Reports: Many states require LLCs to file annual or
              biennial reports and pay a filing fee.
            </li>
            <li>
              Business Licenses and Permits: Ensure you have all necessary
              licenses and permits to operate legally.
            </li>
            <li>
              Registered Agent Requirements: Maintain a registered agent with a
              current address on file with the state.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <JoinBanner />
      <StyledBackgroundImageContainer>
        <picture>
          <source
            srcSet={`${config.basePath}/images/blog-arrow-logo2.svg`}
            type="image/svg+xml"
          />
          <img
            src={`${config.basePath}/images/blog-arrow-logo2.svg`}
            alt="Background Logo"
          />
        </picture>
      </StyledBackgroundImageContainer>
    </StyledContainer>
  );
};

HowToFormAnLLCStepByStepGuideArticle.propTypes = {
  idPrefix: PropTypes.string,
};

export default HowToFormAnLLCStepByStepGuideArticle;
